import React, { useState, useEffect, useRef } from 'react';
import { TextButton } from 'm2m.components';
import { useDispatch } from 'react-redux';
import { addAlert } from 'store/modules/snackBar';
import { AditamentoRevisionModal } from 'components/aditamentoModal/aditamentoRevisionModal';
import { getDivergenciasValidacao, DivergenciasData } from 'requests/divergencias';

interface DivergenciasButtonProps {
  instrumentoFinanceiro: {
    id: number;
    codigo: string;
    tipoInstrumentoFinanceiro: string;
    origem?: string;
    statusValidacao?: string;
    dataDeCriacao: Date;
  };
}

export const DivergenciasButton: React.FC<DivergenciasButtonProps> = ({ instrumentoFinanceiro }) => {
  const dispatch = useDispatch();
  const [openDivergenciasModal, setOpenDivergenciasModal] = useState<boolean>(false);
  const [divergenciasData, setDivergenciasData] = useState<DivergenciasData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  
  const cacheRef = useRef<Map<number, DivergenciasData>>(new Map());

  const tiposComValidacao = ['NOTA_COMERCIAL', 'DEBENTURE'];
  const instrumentoSuportaValidacao = tiposComValidacao.includes(instrumentoFinanceiro.tipoInstrumentoFinanceiro);

  const hasDivergencias = instrumentoSuportaValidacao && 
                         instrumentoFinanceiro.statusValidacao === 'DIVERGENTE' && 
                         (instrumentoFinanceiro.origem === 'FRONTIER' || instrumentoFinanceiro.origem === undefined);

  const hasError = instrumentoSuportaValidacao && 
                   instrumentoFinanceiro.statusValidacao === 'ERRO' && 
                   (instrumentoFinanceiro.origem === 'FRONTIER' || instrumentoFinanceiro.origem === undefined);

  const shouldShowButton = hasDivergencias || hasError;

  useEffect(() => {
    const loadDivergenciasData = async () => {
      if (!dataLoaded && !cacheRef.current.has(instrumentoFinanceiro.id) && (hasDivergencias || hasError)) {
        try {
          const divergenciasData = await getDivergenciasValidacao(
            dispatch,
            { instrumentoFinanceiroId: instrumentoFinanceiro.id.toString() },
            true
          );
          
          cacheRef.current.set(instrumentoFinanceiro.id, divergenciasData);
          setDivergenciasData(divergenciasData);
          setDataLoaded(true);
        } catch (error) {
          console.error('Erro ao pré-carregar dados de validação:', error);
        }
      }
    };

    loadDivergenciasData();
  }, [instrumentoFinanceiro.id, hasDivergencias, hasError, dataLoaded, dispatch]);

  const handleOpenDivergenciasModal = async () => {
    const cachedData = cacheRef.current.get(instrumentoFinanceiro.id);
    
    if (cachedData) {
      setDivergenciasData(cachedData);
      setOpenDivergenciasModal(true);
      return;
    }

    try {
      setLoading(true);
      
      const divergenciasData = await getDivergenciasValidacao(
        dispatch,
        { instrumentoFinanceiroId: instrumentoFinanceiro.id.toString() },
        true
      );
      
      cacheRef.current.set(instrumentoFinanceiro.id, divergenciasData);
      
      setDivergenciasData(divergenciasData);
      setDataLoaded(true);
      setOpenDivergenciasModal(true);
    } catch (error) {
      console.error('Erro ao buscar divergências:', error);
      dispatch(addAlert({ 
        severity: 'error', 
        message: 'Erro ao carregar divergências. Tente novamente.' 
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDivergenciasModal = () => {
    setOpenDivergenciasModal(false);
    setDivergenciasData(null);
  };


  if (!shouldShowButton) {
    return null;
  }

  // Determinar cor e texto do botão baseado no status
  const buttonStyle = hasError ? {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb'
  } : {
    backgroundColor: '#fff3cd',
    color: '#856404',
    border: '1px solid #ffeaa7'
  };

  const buttonText = hasError 
    ? (loading ? '⏳ Carregando...' : '🔄 Ver Validação')
    : (loading ? '⏳ Carregando...' : (dataLoaded ? '🚨 Ver Divergências ✓' : '🚨 Ver Divergências'));

  return (
    <>
      <TextButton
        style={{ 
          fontSize: '0.875rem', 
          padding: '6px 12px',
          borderRadius: '16px',
          marginLeft: '16px',
          minWidth: 'auto',
          ...buttonStyle
        }}
        onClick={handleOpenDivergenciasModal}
        disabled={loading}
      >
        {buttonText}
      </TextButton>
      
      <AditamentoRevisionModal
        open={openDivergenciasModal}
        onClose={handleCloseDivergenciasModal}
        data={null}
        divergenciasData={divergenciasData}
        mode="divergencias"
        allowEdit={false}
      />
    </>
  );
};
